import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from "@mui/material";
import {
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
} from "./catalogGridStyles.js";

const OPTIONS = [
    {
        value: "parent",
        title: "This is the parent listing",
        description:
            "Add the eBay Item number (Listing ID) on this product, save, then remigrate.",
    },
    {
        value: "child",
        title: "This is a child variation",
        description:
            "Open the Parent SKU, add its Listing ID, save and migrate the parent, then retry this child.",
    },
    {
        value: "notOnEbay",
        title: "Product isn’t on eBay yet",
        description:
            "Migrate only links existing listings. Create the listing via Inventory Upload instead.",
    },
];

function truncateMessage(message, max = 220) {
    const text = String(message || "").trim();
    if (!text) return "Migrate failed.";
    if (text.length <= max) return text;
    return `${text.slice(0, max - 1)}…`;
}

/**
 * Guided recovery when Migrate to eBay fails for a product.
 */
export default function MigrateEbayRecoveryDialog({
    open,
    onClose,
    sku = "",
    message = "",
    onChooseParent,
    onRetryChild,
    onNotOnEbay,
}) {
    const [choice, setChoice] = useState("parent");
    const [childSteps, setChildSteps] = useState(false);

    useEffect(() => {
        if (!open) return;
        setChoice("parent");
        setChildSteps(false);
    }, [open]);

    const handlePrimary = () => {
        if (choice === "parent") {
            onChooseParent?.();
            return;
        }
        if (choice === "child") {
            if (!childSteps) {
                setChildSteps(true);
                return;
            }
            onRetryChild?.();
            return;
        }
        onNotOnEbay?.();
    };

    const primaryLabel =
        choice === "parent"
            ? "Add Listing ID"
            : choice === "child"
              ? childSteps
                  ? "Retry this SKU"
                  : "Continue"
              : "Got it";

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>Migrate to eBay failed</DialogTitle>
            <DialogContent>
                <Box
                    sx={{
                        mb: 2,
                        px: 1.5,
                        py: 1.25,
                        borderRadius: "10px",
                        backgroundColor: "#fef3f2",
                        border: "1px solid #fecdca",
                    }}
                >
                    {sku ? (
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#912018" }}>
                            {sku}
                        </Typography>
                    ) : null}
                    <Typography sx={{ fontSize: 13, color: "#b42318", mt: sku ? 0.5 : 0 }}>
                        {truncateMessage(message)}
                    </Typography>
                </Box>

                {childSteps && choice === "child" ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1c2430" }}>
                            Finish these steps, then retry
                        </Typography>
                        <Box
                            component="ol"
                            sx={{ m: 0, pl: 2.5, color: "#5c6570", fontSize: 13, lineHeight: 1.5 }}
                        >
                            <li>Open the Parent SKU product in Products.</li>
                            <li>Paste the eBay Item number into eBay Listing ID and save.</li>
                            <li>Run Migrate to eBay on the parent.</li>
                            <li>Come back here and retry this child variation.</li>
                        </Box>
                    </Box>
                ) : (
                    <>
                        <Typography sx={{ fontSize: 14, color: "#5c6570", mb: 1 }}>
                            Which situation matches this product?
                        </Typography>
                        <RadioGroup
                            value={choice}
                            onChange={(e) => {
                                setChoice(e.target.value);
                                setChildSteps(false);
                            }}
                        >
                            {OPTIONS.map((opt) => (
                                <Box
                                    key={opt.value}
                                    onClick={() => {
                                        setChoice(opt.value);
                                        setChildSteps(false);
                                    }}
                                    sx={{
                                        mb: 1,
                                        px: 1.25,
                                        py: 1,
                                        borderRadius: "10px",
                                        border: "1px solid",
                                        borderColor:
                                            choice === opt.value ? "#0d9488" : "#e2e8f0",
                                        backgroundColor:
                                            choice === opt.value ? "#f0fdfa" : "#fff",
                                        cursor: "pointer",
                                    }}
                                >
                                    <FormControlLabel
                                        value={opt.value}
                                        control={<Radio size="small" />}
                                        label={
                                            <Box sx={{ py: 0.25 }}>
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        fontWeight: 700,
                                                        color: "#1c2430",
                                                    }}
                                                >
                                                    {opt.title}
                                                </Typography>
                                                <Typography
                                                    sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}
                                                >
                                                    {opt.description}
                                                </Typography>
                                            </Box>
                                        }
                                        sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
                                    />
                                </Box>
                            ))}
                        </RadioGroup>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} sx={catalogQuietButtonSx}>
                    Cancel
                </Button>
                <Button onClick={handlePrimary} sx={catalogPrimaryButtonSx}>
                    {primaryLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
